import { Fragment, lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { PageLoader } from 'routes/PrivateRouter';
import { closeChecklistURLs } from 'routes/close/closeURLs';
import { createRelativeTaskRoutes } from 'routes/close/createRelativeTaskRoutes';

import { PermissionGuard } from 'components/PermissionGuard';

import { useCustomerFeatureFlags } from 'hooks/useCustomerFlags';
import { useIsMaximorUser } from 'hooks/useIsMaximorUser';

const Checklist = lazy(() =>
  import('pages/period-close/checklist/Checklist').then((m) => ({
    default: m.Checklist,
  }))
);

const ChecklistV2 = lazy(() =>
  import('pages/period-close/checklist-v2/Checklist').then((m) => ({
    default: m.ChecklistV2,
  }))
);

const ChecklistExpandDrawer = lazy(() =>
  import('pages/period-close/checklist-v2/ChecklistTaskDrawer').then((m) => ({
    default: m.ChecklistExpandDrawer,
  }))
);

const ChecklistRouteElement = () => {
  const {
    FF__IS_FACILIO_CUSTOMER,
    FF__IS_RENTLY_CUSTOMER,
    FF__IS_INVST_CUSTOMER,
  } = useCustomerFeatureFlags();
  const isMaximorUser = useIsMaximorUser();
  const isTargetCustomer =
    FF__IS_FACILIO_CUSTOMER || FF__IS_RENTLY_CUSTOMER || FF__IS_INVST_CUSTOMER;

  // Maximor users on target customers see V2 here so task drawer navigation works
  const showV1 = isTargetCustomer && !isMaximorUser;

  return (
    <PermissionGuard moduleKey="checklist">
      <Suspense fallback={<PageLoader />}>
        {showV1 ? <Checklist /> : <ChecklistV2 />}
      </Suspense>
    </PermissionGuard>
  );
};

export const CloseChecklistRoutes = {
  path: closeChecklistURLs._baseRelativePath,
  children: [
    {
      index: true,
      element: <Navigate to={closeChecklistURLs.list} replace />,
    },
    {
      path: ':view',
      element: <ChecklistRouteElement />,
      children: [
        { path: 'new', element: <Fragment /> },
        ...createRelativeTaskRoutes(ChecklistExpandDrawer),
      ],
    },
  ],
};

const ChecklistV1RouteElement = () => {
  const {
    FF__IS_FACILIO_CUSTOMER,
    FF__IS_RENTLY_CUSTOMER,
    FF__IS_INVST_CUSTOMER,
  } = useCustomerFeatureFlags();
  const isMaximorUser = useIsMaximorUser();
  const isTargetCustomer =
    FF__IS_FACILIO_CUSTOMER || FF__IS_RENTLY_CUSTOMER || FF__IS_INVST_CUSTOMER;

  if (!isTargetCustomer || !isMaximorUser) {
    return <Navigate to={closeChecklistURLs.list} replace />;
  }

  return (
    <PermissionGuard moduleKey="checklist">
      <Suspense fallback={<PageLoader />}>
        <Checklist />
      </Suspense>
    </PermissionGuard>
  );
};

// V1 route for Maximor users on target customer workspaces to compare against V2
export const CloseChecklistV1Routes = {
  path: 'checklist-v1',
  children: [
    {
      index: true,
      element: <Navigate to="/close/checklist-v1/list" replace />,
    },
    {
      path: ':view',
      element: <ChecklistV1RouteElement />,
    },
  ],
};
